import React from "react";

import { Section } from "../PatternSections";
import useSessionContext, { CONTEXT_STATUS } from "./useSessionContext";
import { SESSION_CONTEXT_TEXT, fullWhen } from "./catalogText";

const T = SESSION_CONTEXT_TEXT;
const SKELETON_LINES = 2;

const Skeleton = () => (
  <div className="le-skeleton" aria-hidden="true">
    {Array.from({ length: SKELETON_LINES }, (_, i) => <span key={i} className="le-skeleton-line" />)}
  </div>
);

/** A context value: one line of text, or a list when the server sent several. */
const Value = ({ value }) => (Array.isArray(value)
  ? <ul className="le-plain">{value.map((v, i) => <li key={i}>{v}</li>)}</ul>
  : <span>{value}</span>);

function FirstQuery({ firstQuery, startedBy }) {
  if (!firstQuery || !firstQuery.text) return <p className="le-muted">{T.noFirstQuery}</p>;
  const meta = T.asked(startedBy, fullWhen(firstQuery.at));
  return (
    <div className="le-control">
      <p className="le-text">{firstQuery.text}</p>
      {meta && <p className="le-muted">{meta}</p>}
    </div>
  );
}

function ContextList({ entries }) {
  if (!entries.length) return <p className="le-muted">{T.noContext}</p>;
  return (
    <dl className="le-defs">
      {entries.map((e) => (
        <React.Fragment key={e.key}>
          <dt>{e.label}</dt>
          <dd><Value value={e.value} /></dd>
        </React.Fragment>
      ))}
    </dl>
  );
}

/**
 * The top of a detail popup: the conversation's first query (with who asked it and when) and the context it was opened with,
 * in the server's order and with the server's labels. Loads when the popup opens (useSessionContext); while it loads both
 * sections show a skeleton. A gone conversation, another engineer's conversation, a store that is not connected and a failure
 * (with Retry) each say so in place of the two sections. Nothing is shown for an item without a session.
 */
export default function SessionContext({ sessionId }) {
  const { status, data, retry } = useSessionContext(sessionId);
  if (status === CONTEXT_STATUS.NONE) return null;

  let body;
  if (status === CONTEXT_STATUS.LOADING) {
    body = (
      <>
        <Section label={T.firstQuery}><Skeleton /></Section>
        <Section label={T.context}><Skeleton /></Section>
      </>
    );
  } else if (status === CONTEXT_STATUS.READY) {
    body = (
      <>
        <Section label={T.firstQuery}><FirstQuery firstQuery={data.firstQuery} startedBy={data.startedBy} /></Section>
        <Section label={T.context}><ContextList entries={data.entries} /></Section>
      </>
    );
  } else if (status === CONTEXT_STATUS.GONE) {
    body = <p className="le-note le-note--blocked">{T.gone}</p>;
  } else if (status === CONTEXT_STATUS.FORBIDDEN) {
    body = <p className="le-note le-note--blocked">{T.forbidden}</p>;
  } else if (status === CONTEXT_STATUS.NOT_CONNECTED) {
    body = <p className="le-muted">{T.notConnected}</p>;
  } else {
    body = (
      <div className="le-actions">
        <p className="le-error" role="alert">{T.failed}</p>
        <button type="button" className="le-btn le-btn--sm" onClick={retry}>{T.retry}</button>
      </div>
    );
  }
  return (
    <div className="le-fields" role="group" aria-label={T.label} aria-busy={status === CONTEXT_STATUS.LOADING}>
      {status === CONTEXT_STATUS.LOADING && <span className="sr-only" role="status">{T.loading}</span>}
      {body}
    </div>
  );
}
